import { INPUT_CLASS, LABEL_CLASS } from '@/components/common/node-view-settings-panel';
import { ImageEditAltTextFields } from '@/components/image-library/image-edit-alt-text-fields';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { MAX_IMAGE_WIDTH, MIN_IMAGE_WIDTH } from '@/lib/utils';

interface ImageEditDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    previewSrc: string;
    previewAlt?: string | null;
    previewWidth?: string | number | null;
    previewHeight?: string | number | null;
    maImageId?: number | null;
    currentMaDocumentId?: number | null;
    alt: string | null;
    setAlt: (alt: string | null) => void;
    aspectRatio: number;
    height: string;
    setHeight: (height: string) => void;
    width: string;
    setWidth: (width: string) => void;
    displayType: 'block' | 'inline';
    onDisplayTypeChange: (displayType: 'block' | 'inline') => void;
    pull?: string | null;
    onPullChange: (pull: string | null) => void;
    onCancel: () => void;
    onSave: () => void;
}

export function ImageEditDialog({
    open,
    onOpenChange,
    previewSrc,
    previewAlt,
    previewWidth,
    previewHeight,
    maImageId,
    currentMaDocumentId,
    alt,
    setAlt,
    aspectRatio,
    height,
    setHeight,
    width,
    setWidth,
    displayType,
    onDisplayTypeChange,
    pull,
    onPullChange,
    onCancel,
    onSave,
}: ImageEditDialogProps) {
    const updateHeight = (raw: string) => {
        setHeight(raw);
        const parsed = Number(raw);
        if (!isNaN(parsed) && parsed > 0) {
            setWidth(String(Math.round(aspectRatio * parsed)));
        }
    };

    const updateWidth = (raw: string) => {
        setWidth(raw);
        const parsed = Number(raw);
        if (!isNaN(parsed) && parsed > 0) {
            setHeight(String(Math.round(parsed / aspectRatio)));
        }
    };

    const parsedWidth = Number(width);
    const parsedHeight = Number(height);

    const widthError =
        isNaN(parsedWidth) || parsedWidth < MIN_IMAGE_WIDTH
            ? `Width must be at least ${MIN_IMAGE_WIDTH}px`
            : parsedWidth > MAX_IMAGE_WIDTH
              ? `Width must be at most ${MAX_IMAGE_WIDTH}px`
              : null;

    const heightError = isNaN(parsedHeight) || parsedHeight <= 0 ? 'Height must be a positive number' : null;

    return (
        <Dialog
            open={open}
            onOpenChange={onOpenChange}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Edit Image</DialogTitle>
                </DialogHeader>
                <div className="image-preview">
                    <img
                        src={previewSrc}
                        alt={previewAlt ?? ''}
                        width={previewWidth ?? undefined}
                        height={previewHeight ?? undefined}
                    />
                </div>

                <ImageEditAltTextFields
                    maImageId={maImageId}
                    currentMaDocumentId={currentMaDocumentId}
                    alt={alt}
                    onAltChange={setAlt}
                    previewSrc={previewSrc}
                    previewAlt={previewAlt}
                    showPreview={false}
                    enabled={open}
                />

                <div className="flex flex-row gap-2">
                    <div className="flex flex-col flex-grow">
                        <label className={LABEL_CLASS}>
                            Height
                            <Input
                                id="height"
                                className={`${INPUT_CLASS} ${heightError ? 'border-2 border-red-500' : ''}`}
                                type="text"
                                value={height}
                                onChange={e => updateHeight(e.target.value)}
                            />
                        </label>
                        <p className="text-xs text-red-500 min-h-[1rem]">{heightError}</p>
                    </div>
                    <div className="flex flex-col flex-grow">
                        <label className={LABEL_CLASS}>
                            Width
                            <Input
                                id="width"
                                className={`${INPUT_CLASS} ${widthError ? 'border-2 border-red-500' : ''}`}
                                type="text"
                                value={width}
                                onChange={e => updateWidth(e.target.value)}
                            />
                        </label>
                        <p className="text-xs text-red-500 min-h-[1rem]">{widthError}</p>
                    </div>
                </div>

                <label className={LABEL_CLASS}>
                    Display Type
                    <select
                        id="display-type"
                        className={INPUT_CLASS}
                        value={displayType}
                        onChange={e => onDisplayTypeChange(e.target.value as 'block' | 'inline')}
                    >
                        <option value="block">Block</option>
                        <option value="inline">Inline</option>
                    </select>
                </label>

                {displayType === 'block' && (
                    <label className={LABEL_CLASS}>
                        Alignment
                        <select
                            id="pull"
                            className={INPUT_CLASS}
                            value={pull ?? 'none'}
                            onChange={e => onPullChange(e.target.value === 'none' ? null : e.target.value)}
                        >
                            <option value="none">Center</option>
                            <option value="left">Left</option>
                            <option value="right">Right</option>
                        </select>
                    </label>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={!!widthError || !!heightError}
                        onClick={onSave}
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
